import { useMemo } from "react";
import dayjs from "dayjs";
import {
  getTaskCodeLevel,
  getParentTaskCode,
  isThreeLevelTaskCode,
  isTwoLevelTaskCode,
} from "./taskCodeUtils";
import {
  applySameItpSummaryToTask,
  buildItpSummary,
  buildThreeLevelTaskValueMap,
  getItpRowsForTask,
} from "./itpUtils";
import {
  buildUpdate4DBodyFromTask,
  hasValidUpdate4DBody,
} from "./syncUtils";
import {
  resolveScheduleTaskType,
  shouldShowInTaskTable,
  taskMatchesScheduleType,
} from "./constants";

export const useDerivedTaskData = ({
  scheduleTasks,
  allTasks,
  itpTable,
  filterType,
  fixedType,
}) => {
  const scheduleTaskType = resolveScheduleTaskType(fixedType);
  const isItpView = scheduleTaskType === "itp";

  const summaryByParentCode = useMemo(() => {
    if (!isItpView) return new Map();

    const map = new Map();

    const mainItpTasks = Array.isArray(scheduleTasks)
      ? scheduleTasks.filter((task) => {
          const isItp = taskMatchesScheduleType(task, "itp");
          return isItp && isTwoLevelTaskCode(task?.taskCode);
        })
      : [];

    mainItpTasks.forEach((task) => {
      const parentCode = getParentTaskCode(task?.taskCode);

      if (!parentCode) return;

      const relatedItpRows = getItpRowsForTask(task, itpTable);
      const itpSummary = buildItpSummary(relatedItpRows);

      map.set(parentCode, itpSummary);
    });

    return map;
  }, [scheduleTasks, itpTable, isItpView]);

  const filteredTasks = useMemo(() => {
    let tasks = Array.isArray(scheduleTasks)
      ? scheduleTasks.filter((task) => !task.isArchived)
      : [];

    tasks = tasks.filter((task) =>
      shouldShowInTaskTable(task, scheduleTaskType),
    );

    if (isItpView) {
      tasks = tasks.map((task) =>
        applySameItpSummaryToTask(task, summaryByParentCode)
      );
    }

    const today = dayjs();

    if (filterType === "dueToday") {
      tasks = tasks.filter(
        (task) => task.targetDate && dayjs(task.targetDate).isSame(today, "day")
      );
    } else if (filterType === "overdue") {
      tasks = tasks.filter(
        (task) =>
          task.targetDate &&
          dayjs(task.targetDate).isBefore(today, "day") &&
          Number(task.progress) < 100
      );
    } else if (filterType === "highPriority") {
      tasks = tasks.filter((task) => task.priority === "High");
    }

    return tasks;
  }, [
    filterType,
    scheduleTasks,
    summaryByParentCode,
    isItpView,
    scheduleTaskType,
  ]);

  const allTasksWithItpValues = useMemo(() => {
    const tasks = Array.isArray(allTasks) ? allTasks : [];

    return tasks.map((task) => {
      const type = String(task?.type || "").toLowerCase();

      if (type !== "itp") {
        return task;
      }

      const level = getTaskCodeLevel(task?.taskCode);

      if (level !== 2 && level !== 3) {
        return task;
      }

      return applySameItpSummaryToTask(task, summaryByParentCode);
    });
  }, [allTasks, summaryByParentCode]);

  const tasksToSync4D = useMemo(() => {
    const tasks = Array.isArray(allTasksWithItpValues)
      ? allTasksWithItpValues
      : [];

    return tasks
      .filter((task) => {
        const type = String(task?.type || "").toLowerCase();
        const level = getTaskCodeLevel(task?.taskCode);

        return (
          type === "itp" &&
          task?.id &&
          task.__itpSummaryApplied === true &&
          (level === 2 || level === 3)
        );
      })
      .map((task) => {
        const body = buildUpdate4DBodyFromTask(task);

        return {
          task,
          body,
        };
      })
      .filter(({ body }) => hasValidUpdate4DBody(body));
  }, [allTasksWithItpValues]);

  const threeLevelTaskValuesByParentCode = useMemo(() => {
    return buildThreeLevelTaskValueMap(allTasksWithItpValues);
  }, [allTasksWithItpValues]);

  const { totalActualHours, averageProgress } = useMemo(() => {
    if (!Array.isArray(filteredTasks) || filteredTasks.length === 0) {
      return {
        totalActualHours: 0,
        averageProgress: 0,
      };
    }

    let totalActual = 0;
    let totalProg = 0;
    let count = 0;

    filteredTasks.forEach((task) => {
      const actual = parseFloat(
        task.manhours !== undefined && task.manhours !== null
          ? task.manhours
          : task.hours
      );

      if (!isNaN(actual)) {
        totalActual += actual;
      }

      const prog = parseFloat(task.progress);

      if (!isNaN(prog)) {
        totalProg += prog;
        count += 1;
      }
    });

    return {
      totalActualHours: totalActual,
      averageProgress: count ? totalProg / count : 0,
    };
  }, [filteredTasks]);

  return {
    summaryByParentCode,
    filteredTasks,
    allTasksWithItpValues,
    tasksToSync4D,
    threeLevelTaskValuesByParentCode,
    totalActualHours,
    averageProgress,
  };
};
